import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import axios from "axios";
import { useAlert } from "react-alert";
import { useApiError } from "../../hooks/useApiError.js";

import { SettingsContext } from "../../contexts";

import "./agreement-signatory-panel.scss";

/**
 * Primary Admin → Agreement Signatory.
 *
 * Sets the name + title printed as the signatory on the purchase / consignment
 * agreement PDFs (replacing the hardcoded "Guillaume Barb / Founder & CEO"),
 * plus the SIGNATURE IMAGE printed under "Signature of Consignee" on the
 * consignment agreement — which used to be one hardcoded image shared by every
 * store, i.e. another company's signature on a tenant's contract.
 *
 * A store's own Primary Admin sets THEIR store's signatory; an RC master admin
 * can search any store and set it. Saves via the self-scoped endpoint
 * /api/store/agreement-signatory — the backend forces a non-master to their own
 * store regardless of what the UI sends, so there is no cross-tenant write.
 * Mirrors BaseCurrencyPanel.
 *
 * The signature is submitted as multipart FormData over POST (not PATCH): PHP
 * does not populate $_FILES for PATCH requests, so every file upload in this app
 * POSTs — see the Email Branding logo upload in MasterAdminSettings. The backend
 * registers POST alongside the original PATCH for exactly this reason.
 *
 * The current signature is read from GET /api/store/agreement-signatory, which
 * returns it inline as a data URI. It is deliberately NOT on /api/app-settings:
 * that route is public and returns on every bootstrap, so a base64 image there
 * would both leak and bloat it. The master's store-search endpoint only reports
 * `has_signature`, so the image itself is fetched when a store is picked.
 */
const MAX_SIGNATURE_KB = 1024;
// Raster only: dompdf renders SVG unreliably, so an accepted SVG could come out
// BLANK on the agreement — a silently unsigned contract. Backend validation
// matches (mimes:png,jpg,jpeg).
const ACCEPTED_TYPES = "image/png,image/jpeg,image/jpg";

export function AgreementSignatoryPanel({ user }) {
  const { settings, reloadSettings } = useContext(SettingsContext);
  const alert = useAlert();
  const { showApiError } = useApiError();
  const isMaster = !!user?.is_master_admin;

  // Master: search + pick a store. Owner: implicit own store.
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null); // { id, name, signatory_name, … }
  const [name, setName] = useState("");
  const [title, setTitle] = useState("");
  const [saving, setSaving] = useState(false);

  // Signature: what the server currently has, what the user just picked, and
  // whether they asked for the stored one to be removed.
  const [savedSignature, setSavedSignature] = useState(null); // data URI | null
  const [pendingFile, setPendingFile] = useState(null);
  const [pendingPreview, setPendingPreview] = useState(null); // object URL
  const [removeSignature, setRemoveSignature] = useState(false);
  const fileInputRef = useRef(null);

  const clearPendingFile = useCallback(() => {
    setPendingFile(null);
    setPendingPreview((url) => {
      if (url) URL.revokeObjectURL(url);
      return null;
    });
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, []);

  /**
   * Load the current signatory + signature for a store. store_id is only
   * honoured for a master admin — the backend ignores it for anyone else and
   * resolves the caller's own store.
   */
  const loadCurrent = useCallback(async (storeId) => {
    try {
      const { data } = await axios.get("/api/store/agreement-signatory", {
        params: storeId ? { store_id: storeId } : {},
      });
      setName(data.signatory_name || "");
      setTitle(data.signatory_title || "");
      setSavedSignature(data.signature_data_uri || null);
      setRemoveSignature(false);
    } catch (e) {
      // Non-fatal — name/title still seed from SettingsContext for an owner.
      setSavedSignature(null);
    }
  }, []);

  // Owner: seed name/title from their own store's resolved values (fast paint,
  // already in the bootstrap payload).
  useEffect(() => {
    if (isMaster) return;
    setName(settings?.signatory_name || "");
    setTitle(settings?.signatory_title || "");
  }, [isMaster, settings?.signatory_name, settings?.signatory_title]);

  // Owner: then load the signature, which app-settings deliberately omits.
  useEffect(() => {
    if (isMaster) return;
    loadCurrent(null);
  }, [isMaster, loadCurrent]);

  // Release the object URL when it changes or the panel unmounts.
  useEffect(
    () => () => {
      if (pendingPreview) URL.revokeObjectURL(pendingPreview);
    },
    [pendingPreview],
  );

  const searchStores = async (term) => {
    setQ(term);
    setSelected(null);
    setSavedSignature(null);
    setRemoveSignature(false);
    clearPendingFile();
    if (!term || term.trim().length < 2) {
      setResults([]);
      return;
    }
    try {
      const { data } = await axios.get("/api/admin/stores/branding", { params: { q: term } });
      setResults(data.items || []);
    } catch (e) {
      alert.show("Could not search stores.");
    }
  };

  const pickStore = (s) => {
    setSelected(s);
    setName(s.signatory_name || "");
    setTitle(s.signatory_title || "");
    setResults([]);
    setQ(`${s.name || "Store"} (#${s.id})`);
    clearPendingFile();
    // The search endpoint only reports has_signature — fetch the image itself.
    loadCurrent(s.id);
  };

  const onPickFile = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    if (file.size > MAX_SIGNATURE_KB * 1024) {
      alert.show(`Signature image must be ${MAX_SIGNATURE_KB} KB or smaller.`);
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    if (pendingPreview) URL.revokeObjectURL(pendingPreview);
    setPendingFile(file);
    setPendingPreview(URL.createObjectURL(file));
    setRemoveSignature(false);
  };

  // Remove: if the user had only staged a new file, drop it; otherwise flag the
  // stored signature for removal on save.
  const onRemove = () => {
    if (pendingFile) {
      clearPendingFile();
      return;
    }
    if (savedSignature) setRemoveSignature(true);
  };

  const currentName = isMaster ? (selected?.signatory_name || "") : (settings?.signatory_name || "");
  const currentTitle = isMaster ? (selected?.signatory_title || "") : (settings?.signatory_title || "");

  // The staged file wins; a staged removal shows nothing.
  const previewSrc = pendingPreview || (removeSignature ? null : savedSignature);

  const textChanged = name.trim() !== currentName.trim() || title.trim() !== currentTitle.trim();
  const signatureChanged = !!pendingFile || removeSignature;

  const save = async () => {
    if (!textChanged && !signatureChanged) {
      alert.show("No change to save.");
      return;
    }
    setSaving(true);
    try {
      // Multipart over POST — see the note at the top of this file.
      const fd = new FormData();
      fd.append("signatory_name", name.trim());
      fd.append("signatory_title", title.trim());
      if (isMaster && selected) fd.append("store_id", String(selected.id));
      if (pendingFile) {
        fd.append("signature", pendingFile, pendingFile.name);
      } else if (removeSignature) {
        // Only ever sent as "1" — the backend validates it as a boolean, and an
        // empty string would fail that rule.
        fd.append("remove_signature", "1");
      }

      const { data } = await axios.post("/api/store/agreement-signatory", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      alert.show("Agreement signatory saved.");
      clearPendingFile();
      setRemoveSignature(false);
      setSavedSignature(data.signature_data_uri || null);
      setName(data.signatory_name || "");
      setTitle(data.signatory_title || "");

      if (isMaster && selected) {
        setSelected({
          ...selected,
          signatory_name: data.signatory_name,
          signatory_title: data.signatory_title,
          has_signature: !!data.has_signature,
        });
      } else if (typeof reloadSettings === "function") {
        reloadSettings();
      }
    } catch (e) {
      showApiError(e, "save the agreement signatory");
    } finally {
      setSaving(false);
    }
  };

  const showFields = !isMaster || !!selected;
  const canRemove = !!pendingFile || (!!savedSignature && !removeSignature);

  return (
    <div className="ma-sig">
      <p className="ma-sig__intro">
        The name, title and signature image printed as the signatory at the
        bottom of your purchase and consignment agreement PDFs. Leave the name
        and title blank to use your store name. With no signature image
        uploaded, the consignment agreement prints the signature line only.
      </p>

      {isMaster && (
        <div className="ma-sig__store">
          <label className="ma-sig__label">Store</label>
          <input
            type="text"
            value={q}
            placeholder="Search a store by name or id…"
            onChange={(e) => searchStores(e.target.value)}
          />
          {results.length > 0 && (
            <ul className="ma-sig__results">
              {results.map((s) => (
                <li key={s.id}>
                  <button type="button" onClick={() => pickStore(s)}>
                    {s.name || "Store"} (#{s.id})
                    {s.signatory_name ? ` — ${s.signatory_name}` : ""}
                    {s.has_signature ? " (signed)" : ""}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {showFields && (
        <div className="ma-sig__set">
          <label className="ma-sig__label">
            Signatory{isMaster && selected ? ` for ${selected.name || `store ${selected.id}`}` : ""}
          </label>
          <input
            type="text"
            value={name}
            placeholder="Signatory name (e.g. Jane Smith)"
            onChange={(e) => setName(e.target.value)}
          />
          <input
            type="text"
            value={title}
            placeholder="Signatory title (e.g. Managing Director)"
            onChange={(e) => setTitle(e.target.value)}
          />

          <label className="ma-sig__label ma-sig__label--spaced">Signature image</label>
          <div className="ma-sig__sig">
            {previewSrc ? (
              // The box is 150px wide — the size the image renders at in the PDF.
              <div className="ma-sig__preview">
                <img src={previewSrc} alt="Agreement signature" />
              </div>
            ) : (
              <div className="ma-sig__preview ma-sig__preview--empty">
                <span>{removeSignature ? "Will be removed on save" : "No signature uploaded"}</span>
              </div>
            )}

            <div className="ma-sig__sig-actions">
              <input
                ref={fileInputRef}
                type="file"
                className="ma-sig__file"
                accept={ACCEPTED_TYPES}
                onChange={onPickFile}
              />
              {canRemove && (
                <button type="button" className="ma-sig__remove" onClick={onRemove}>
                  Remove signature
                </button>
              )}
              <p className="ma-sig__hint">
                PNG or JPG, up to {MAX_SIGNATURE_KB} KB. A transparent PNG
                reproduces best. Printed 150px wide on the agreement.
              </p>
            </div>
          </div>

          <button type="button" className="rc-btn" disabled={saving} onClick={save}>
            {saving ? "Saving…" : "Save signatory"}
          </button>
        </div>
      )}
    </div>
  );
}
